import { useCallback, useEffect, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';

export type DefaultFieldState = Record<string, string>;

interface UsePdfFieldsProps<
  T extends Record<string, string> = DefaultFieldState,
> {
  containerRef: HTMLElement | null;
  onChange?: (fieldState: T) => void;
  getValuesOnInitialize?: boolean;
  onInitialize?: (fieldState: T) => void;
}

export function usePdfFields<
  T extends Record<string, string> = DefaultFieldState,
>({
  containerRef,
  onChange,
  getValuesOnInitialize,
  onInitialize,
}: UsePdfFieldsProps<T>) {
  const [fieldState, setFieldState] = useState<T>({} as T);

  const initializeFieldsFromPdf = useCallback(
    async (pdf: PDFDocumentProxy): Promise<void> => {
      try {
        const fields = await pdf.getFieldObjects();
        if (!fields) return;
        const initialValues = Object.fromEntries(
          Object.entries(fields).map(([key, field]) => {
            // Extract the first value from each field
            // @ts-expect-error bad pdfjs types
            const firstValue = field[0]?.value as string;
            return [key, firstValue];
          }),
        ) as T;

        setFieldState(initialValues);
        if (getValuesOnInitialize) onChange?.(initialValues);
        onInitialize?.(initialValues);
      } catch (err) {
        console.error('Failed to extract form fields from PDF', err);
      }
    },
    [onChange, getValuesOnInitialize, onInitialize],
  );
  // Watch for input fields and track changes
  useEffect(() => {
    if (!containerRef) return;

    // TODO - consider debouncing this
    const handleInputChange = (e: Event) => {
      const target = e.target as HTMLInputElement | HTMLSelectElement;
      const name = target.name;
      const value = target.value;
      if (!name) return;
      setFieldState((prev) => {
        const newState = { ...prev, [name]: value } as T;
        onChange?.(newState);
        return newState;
      });
    };

    const attachListeners = () => {
      if (!containerRef) return;

      // Query all input fields with name attributes
      const inputs =
        containerRef.querySelectorAll<HTMLInputElement>('input[name]');
      inputs.forEach((input) => {
        input.removeEventListener('input', handleInputChange);
        input.addEventListener('input', handleInputChange);
      });

      // Query all select fields with name attributes
      const selects =
        containerRef.querySelectorAll<HTMLSelectElement>('select[name]');
      selects.forEach((select) => {
        select.removeEventListener('change', handleInputChange);
        select.addEventListener('change', handleInputChange);
      });

      const textareas =
        containerRef.querySelectorAll<HTMLTextAreaElement>('textarea[name]');
      textareas.forEach((textarea) => {
        textarea.removeEventListener('input', handleInputChange);
        textarea.addEventListener('input', handleInputChange);
      });
    };

    const observer = new MutationObserver(() => {
      attachListeners();
    });

    observer.observe(containerRef, {
      childList: true,
      subtree: true,
    });

    attachListeners();

    // Clean up listeners and observer
    return () => {
      observer.disconnect();
      if (!containerRef) return;
      const inputs =
        containerRef.querySelectorAll<HTMLInputElement>('input[name]');
      inputs.forEach((input) => {
        input.removeEventListener('input', handleInputChange);
      });
      const selects =
        containerRef.querySelectorAll<HTMLSelectElement>('select[name]');
      selects.forEach((select) => {
        select.removeEventListener('change', handleInputChange);
      });
      const textareas =
        containerRef.querySelectorAll<HTMLTextAreaElement>('textarea[name]');
      textareas.forEach((textarea) => {
        textarea.removeEventListener('input', handleInputChange);
      });
    };
  }, [containerRef, onChange]);

  return {
    fieldState,
    setFieldState,
    initializeFieldsFromPdf,
  };
}
